import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { Outlet, useNavigate, useLocation, matchPath } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { postRequestUnAuthenticated } from "./../../../services/rest";
import { fetchMyData, clearMyData } from "../../../redux/slices/myDataSlice";
import { setTheme } from "../../../redux/slices/themeSlice";
import {
  initializeEmbedAuth,
  checkAutoLogin,
  embedJwtAuth,
  logOutEmbed,
} from "../embedSlices/embedAuthSlice";
import { setEmbedItem } from "../embedSlices/embedHomeSlice";
import Modals from "./../../../utils/modals";
import EmbedHeaderPage from "./EmbedHeaderPage";
import BotChat from "./BotChat";
import { toast } from "../../../utils/toast";
import {
  fetchEmbedConfig,
  applyEmbedTheme,
  applyInitialEmbedTheme,
  applyEmbedAccent,
  applyEmbedFavicon,
  getAppPrefix,
  getEmbedLastKey,
} from "../utility/embedHelper";

const EmbedHomePage = () => {
  const [loading, setLoading] = useState(false);
  const [embedLoadTimeout, setEmbedLoadTimeout] = useState(false);
  const [readyToRender, setReadyToRender] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Explicit backend failure (invalid key / no channels) — distinct from a
  // transient slow-connection timeout, and never triggers the reload loop.
  const [embedError, setEmbedError] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const embedDataReceivedRef = useRef(false);

  // Apply the embed theme synchronously before the first paint so the loading
  // screen never flashes light while the config request is still in flight.
  useLayoutEffect(() => {
    applyInitialEmbedTheme();
  }, []);

  const fetchData = async () => {
    try {
      const storedEmbedData = localStorage.getItem("embedData");
      if (!storedEmbedData) return;

      const { apiKey, selectedChannel, selectedTopic, domain, channels } =
        JSON.parse(storedEmbedData);

      const formData = new FormData();
      formData.append("apiKey", apiKey);
      formData.append("selectedChannel", selectedChannel || "");
      formData.append("selectedTopic", selectedTopic || "");
      formData.append("domain", domain || "");
      formData.append("echannels", JSON.stringify(channels));

      const response = await postRequestUnAuthenticated(
        "/generate/embed-data",
        formData
      );

      if (response.success) {
        localStorage.setItem("embedFetchedData", JSON.stringify(response));
        dispatch(setEmbedItem({ field: "channels", value: response.channels }));
        dispatch(
          setEmbedItem({
            field: "selectedChannel",
            value: response.selectedChannel,
          })
        );
        dispatch(
          setEmbedItem({
            field: "selectedTopic",
            value: response.selectedTopic,
          })
        );
        dispatch(setEmbedItem({ field: "username", value: response.username }));

        embedDataReceivedRef.current = true;
        localStorage.removeItem("embedReloadAttempts");
      } else {
        console.warn("Failed to fetch embed data:", response);
        // Mark as "received" so the slow-connection reload loop stays off, and
        // surface a clear, non-retrying message instead.
        embedDataReceivedRef.current = true;
        localStorage.removeItem("embedReloadAttempts");
        const msg = (response && (response.message || response.error)) || "";
        if (response && response.noAccess) {
          // Subscription lapsed or member cap reached — show the admin-facing
          // message verbatim instead of a generic error.
          setEmbedError({
            title: response.atCap ? "Community is full" : "Community unavailable",
            message:
              msg ||
              "This community isn't available right now. Please contact your administrator.",
          });
        } else if (/no channels/i.test(msg)) {
          setEmbedError({
            title: "No channels yet",
            message:
              "This community doesn't have any channels to show right now. Check back soon.",
          });
        } else {
          setEmbedError({
            title: "Couldn't load this community",
            message:
              "The embed key may be invalid or this domain isn't verified yet.",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching embed data:", error);
    }
  };

  const navigateToChannelFromStorage = () => {
    const stored = localStorage.getItem("embedFetchedData");
    const authData = localStorage.getItem("auth-token");
    if (!stored) return;
    // Respect the URL the user is already on. This redirect is only meant to
    // pick a default channel when landing on the bare embed root — if we're
    // already on a channel/topic/messages/profile route (deep link, reload, or
    // an in-app navigation), bouncing to the default channel is wrong.
    if (
      typeof window !== "undefined" &&
      (/\/user\/[^/]+\/(channel|messages|profile)/.test(
        window.location.pathname
      ) ||
        /\/embed\/channels\/event\//.test(window.location.pathname))
    ) {
      return;
    }
    const data = JSON.parse(stored);

    // Prefer the last channel/topic this user opened (per domain) over the
    // business default, so the embed reopens where they left off.
    let channel = data.selectedChannel;
    let topic = data.selectedTopic;
    try {
      const key = getEmbedLastKey();
      const last = key ? JSON.parse(localStorage.getItem(key) || "{}") : {};
      if (last.channel) {
        channel = last.channel;
        topic = last.topic || "";
      }
    } catch (e) {}

    if (!authData) {
      navigate(`/embed/channels/user/${data.username}/channel/${channel}`);
    } else if (channel && topic) {
      navigate(
        `/embed/channels/user/${data.username}/channel/${channel}/c-id/topic/${topic}`
      );
    } else if (channel) {
      navigate(`/embed/channels/user/${data.username}/channel/${channel}`);
    }
  };

  const processEmbedData = useCallback(
    async (embedData) => {
      if (!embedData) return;
      embedDataReceivedRef.current = true;
      try {
        setLoading(true);
          localStorage.setItem("embedData", JSON.stringify(embedData));
          // Apply the business's EmbedConfig: theme mode + accent + branding/features.
          const cfg = await fetchEmbedConfig(embedData.apiKey);
          const themeMode =
            cfg?.branding?.themeMode || embedData.theme || "light";
          // applyEmbedTheme themes the embed's OWN document only. Do NOT write
          // the shared localStorage "theme" or global redux theme — that bled
          // the embed's theme into the website/admin (same origin).
          applyEmbedTheme(themeMode);
          applyEmbedAccent(
            cfg?.branding?.accentColor,
            cfg?.branding?.accentTextColor
          );
          applyEmbedFavicon(cfg?.branding);
          dispatch(setEmbedItem({ field: "branding", value: cfg?.branding || {} }));
          dispatch(setEmbedItem({ field: "features", value: cfg?.features || {} }));
          dispatch(setEmbedItem({ field: "bot", value: cfg?.bot || {} }));
          // Sign-in mode drives whether we silently log in or wait for the
          // user to authenticate. Persist it so the auth page can read it too.
          const authMode = cfg?.auth?.mode || "otp";
          localStorage.setItem("embedAuthMode", authMode);
          dispatch(setEmbedItem({ field: "authMode", value: authMode }));
          // NOTE: do NOT log out here. otp/google have no silent re-login, so
          // wiping the session on every bootstrap forced members to sign in
          // again each time the embed reopened. trusted/jwt re-authenticate
          // below and simply overwrite the session with the passed identity.

          // Apply a successful auth result to local session + redux.
          const applyAuth = (authResult) => {
            if (authResult && authResult.token && authResult.user) {
              const user = authResult.user;
              localStorage.setItem(
                "user",
                JSON.stringify({
                  _id: user._id,
                  username: user.username,
                  name: user.name,
                  email: user.email,
                  contact: user.contact,
                  whatsapp_number: user.whatsapp_number,
                })
              );
              localStorage.setItem("auth-token", authResult.token);
              dispatch(fetchMyData());
            }
            // Plan user-cap hit on join — let the member know softly.
            if (authResult?.channelFull) {
              toast(
                "This channel is filling up — please try again later.",
                "error"
              );
            }
          };

          if (authMode === "trusted") {
            // Trusted passthrough — log in from the details the business passed.
            const formData = new FormData();
            formData.append("email", embedData.email || "");
            formData.append("phone", embedData.phone || "");
            formData.append("external_id", embedData.external_id || "");
            formData.append("name", embedData.name || "");
            formData.append("image", embedData.image || "");
            formData.append("domain", embedData.domain);
            formData.append("apiKey", embedData.apiKey);
            formData.append("channelName", embedData.selectedChannel);
            formData.append("autoLogin", true);
            const authResult = await dispatch(checkAutoLogin(formData))
              .unwrap()
              .catch(() => null);
            applyAuth(authResult);
          } else if (authMode === "jwt" && embedData.token) {
            // JWT single sign-on — verify the business-signed handoff token.
            const authResult = await dispatch(
              embedJwtAuth({
                token: embedData.token,
                apiKey: embedData.apiKey,
                domain: embedData.domain,
                channel: embedData.selectedChannel,
              })
            )
              .unwrap()
              .catch(() => null);
            applyAuth(authResult);
          }
          // google / otp: no silent login — the member signs in via the auth
          // page (get-started) when they act, using only the configured method.

          dispatch(initializeEmbedAuth());
          // Restore the member's profile for a persisted (otp/google) session so
          // they stay logged in across embed open/close without re-auth.
          if (localStorage.getItem("auth-token")) {
            dispatch(fetchMyData());
          }
          await fetchData();
          setReadyToRender(true);
          navigateToChannelFromStorage();
        } catch (err) {
          console.error("Error handling embedData:", err);
        } finally {
          setLoading(false);
          // Never leave the user on an infinite spinner — once we've processed
          // (or failed) a bootstrap, reveal the app (or the error UI).
          setReadyToRender(true);
        }
    },
    [dispatch]
  );

  useEffect(() => {
    const handleMessage = (event) => {
      const message = event.data;
      if (
        typeof message === "object" &&
        message.type === "embedData" &&
        message.source === "channels-widget"
      ) {
        processEmbedData(message.payload);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [processEmbedData]);

  // Native SDK deep-link: the React Native app posts this into the WebView when
  // a push notification is tapped, so we jump to the right channel/topic. The
  // message may arrive as a JSON string (RN injected postMessage) or an object.
  useEffect(() => {
    const handleNative = (event) => {
      let msg = event.data;
      if (typeof msg === "string") {
        try {
          msg = JSON.parse(msg);
        } catch (e) {
          return;
        }
      }
      if (
        !msg ||
        typeof msg !== "object" ||
        msg.source !== "channels-native" ||
        msg.action !== "open"
      ) {
        return;
      }
      let username = "";
      try {
        username =
          JSON.parse(localStorage.getItem("embedData") || "{}").username || "";
      } catch (e) {}
      if (!username || !msg.channel) return; // DM/brand (roomId) nav is a follow-up
      if (msg.topic) {
        navigate(
          `/embed/channels/user/${username}/channel/${msg.channel}/c-id/topic/${msg.topic}`
        );
      } else {
        navigate(`/embed/channels/user/${username}/channel/${msg.channel}`);
      }
    };
    window.addEventListener("message", handleNative);
    return () => window.removeEventListener("message", handleNative);
  }, [navigate]);

  // Resilience for re-open: when the host page is refreshed and the embed is
  // opened again, the iframe reloads fresh and waits for the widget's
  // postMessage. If that post is missed (timing/cache), the embed would hang on
  // the spinner forever. So if no message has arrived shortly after mount but we
  // have embedData cached from a previous open, bootstrap straight from it.
  useEffect(() => {
    // A stale reload counter must never permanently pin the embed shut.
    localStorage.removeItem("embedReloadAttempts");
    const t = setTimeout(() => {
      if (!embedDataReceivedRef.current) {
        const stored = localStorage.getItem("embedData");
        if (stored) {
          try {
            processEmbedData(JSON.parse(stored));
          } catch (e) {}
        }
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [processEmbedData]);

  // Direct-load path: open the embed via URL params with no parent widget, e.g.
  //   /embed/channels?apiKey=...&domain=...&channel=general&theme=dark
  // We synthesize the same embedData message the widget would post, so the
  // normal flow above runs unchanged (used for the SDK WebView + dev/testing).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const apiKey = params.get("apiKey");
    if (!apiKey) return;
    // A apiKey in the URL is authoritative. If stored embedData is for a
    // DIFFERENT key (stale from a previous session/business), clear it and
    // re-bootstrap — otherwise the embed would silently load the old key.
    const stored = localStorage.getItem("embedData");
    if (stored) {
      let storedKey = null;
      try { storedKey = JSON.parse(stored).apiKey; } catch (e) {}
      if (storedKey === apiKey) return; // already bootstrapped for this key
      localStorage.removeItem("embedData");
      localStorage.removeItem("embedFetchedData");
    }
    window.postMessage(
      {
        type: "embedData",
        source: "channels-widget",
        payload: {
          apiKey,
          domain: params.get("domain") || "",
          selectedChannel:
            params.get("channel") || params.get("selectedChannel") || "",
          selectedTopic:
            params.get("topic") || params.get("selectedTopic") || "",
          channels: [],
          email: params.get("email") || "",
          external_id: params.get("external_id") || "",
          token: params.get("token") || "",
          name: params.get("name") || "",
          image: params.get("image") || "",
          autoLogin: params.get("autoLogin") === "true",
          theme: params.get("theme") || "light",
        },
      },
      "*"
    );
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!embedDataReceivedRef.current) {
        setEmbedLoadTimeout(true);
      }
    }, 4000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (embedLoadTimeout && !embedError) {
      const attempts = parseInt(
        localStorage.getItem("embedReloadAttempts") || "0"
      );
      if (attempts < 3) {
        localStorage.setItem("embedReloadAttempts", attempts + 1);
        setTimeout(() => window.location.reload(), 2000);
      } else {
        console.warn("Max reload attempts reached");
      }
    }
  }, [embedLoadTimeout]);

  const closeSidebar = () => setIsSidebarOpen(false);

  const location = useLocation();
  // The bare channel page (no topic yet) renders a full-bleed cover; on it the
  // header floats over the cover with a scrim instead of sitting in a flat band
  // above it. Everywhere else (topic chat, messages) the header stays in-flow.
  const isCoverPage = !!matchPath(
    `${getAppPrefix()}/user/:username/channel/:channelId`,
    location.pathname
  );
  const headerVisible = !embedLoadTimeout && !loading && !embedError;

  return (
    <div className="flex flex-col h-full w-full overflow-hidden relative bg-theme-secondaryBackground">
      {headerVisible && !isCoverPage && (
        <div className="flex w-full">
          <EmbedHeaderPage />
        </div>
      )}

      <div className="relative flex flex-1 overflow-hidden ">
        {headerVisible && isCoverPage && (
          <div className="absolute inset-x-0 top-0 z-30">
            <EmbedHeaderPage overlay />
          </div>
        )}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-30 sm:hidden"
            onClick={closeSidebar}
          ></div>
        )}

        {/* On cover pages the header floats (absolute), so the content area is
            the full height; subtracting the 56px header here would leave a dead
            gap at the bottom. Elsewhere the header is in-flow, so keep the
            reduced height. */}
        <div className={`w-full ${isCoverPage ? "h-full" : "h-full-height-56"}`}>
          {embedError ? (
            <div className="flex h-full flex-col items-center justify-center px-6 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-theme-tertiaryBackground text-2xl text-theme-secondaryText">
                ⌗
              </div>
              <p className="mb-1 text-lg font-semibold text-theme-secondaryText">
                {embedError.title}
              </p>
              <p className="max-w-sm text-sm text-theme-placeholder">
                {embedError.message}
              </p>
            </div>
          ) : loading || !readyToRender ? (
            <div className="flex flex-col items-center justify-center mt-20 text-center">
              <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin border-theme-secondaryText"></div>
              <p className="mt-4 text-2xl font-normal text-theme-secondaryText">
                Loading...
              </p>
            </div>
          ) : embedLoadTimeout ? (
            <div className="flex h-full flex-col items-center justify-center px-6 text-center">
              <p className="mb-3 text-sm text-theme-placeholder">
                This is taking longer than usual. Check your connection and try
                again.
              </p>
              <button
                className="rounded-lg bg-theme-accent px-4 py-2 text-sm font-semibold text-theme-accentText transition-colors hover:bg-theme-accentHover"
                onClick={() => window.location.reload()}
              >
                Reload
              </button>
            </div>
          ) : (
            <Outlet />
          )}
          <Modals />
        </div>
      </div>
      {/* First-time member welcome assistant (self-gates on a per-member flag). */}
      {/* Old one-shot WelcomeBot retired — the Bot Studio bot now handles the
          welcome via its welcomeNewJoiners trigger. */}
      {!embedError && !loading && readyToRender && <BotChat />}
    </div>
  );
};

export default EmbedHomePage;
